/**
 * NUDIENT: Sökningen efter vald modell, ändring av F2, M-121 punkt B.
 *
 * När kunden har valt en modell visas produkterna för den modellen först,
 * utan att resten döljs. Utan valt modell händer ingenting, som förut. Bara
 * ett sparat val räknas, inte standardmodellen, M-007.
 *
 * Bara när sökningen handlar om skal, M-144: fler än hälften av de tolv
 * främsta produkterna har en telefonmodell, ur modellminnets poster, bland
 * sina Compatible models. Söker kunden på airpods, cabin eller annat gäller
 * Shopifys ordning, och ingen grupp visas.
 *
 * 1. Sökrutans förslag: Horizons predictive search ritar högst åtta av de tio
 *    produkter Shopify skickar. Varje produkt har data-nudient-models med sina
 *    Compatible models, F2-H2 i snippets/predictive-search-products-list.liquid.
 *    Produkterna som passar vald modell läggs överst i Shopifys ordning, bara
 *    bland Shopifys egna förslag för sökordet, M-144. Det extra anropet med
 *    modellens namn, som tog med produkter som bara matchade modellen, är
 *    borttaget.
 * 2. Sökresultatsidan: sidan har en sökning. Har adressen inget modellfilter
 *    hämtas samma sökning med modellfiltret en gång, med Shopifys Section
 *    Rendering API och sektionens id, och de riktiga träffarna, kort med
 *    data-nudient-match ur snippets/nudient-search-match.liquid, läggs som
 *    gruppen "For your [modell]" överst i resultaten, M-144. Samma produkter
 *    tas bort ur listan under på sidan. Har adressen modellfiltret, eller ger
 *    gruppen inga träffar, händer ingenting. Byter kunden filter ritar
 *    Horizon om resultaten, och gruppen försvinner.
 *    Byter kunden modell i väljaren, model:change, byggs gruppen om för den
 *    nya modellen, och listans dolda kort visas igen först, M-142. En
 *    hämtning som blir inaktuell av ett nytt byte används inte.
 *    Över listan under gruppen står "All results", nudient.search.all_results,
 *    i gruppens rubrikstil, bara när gruppen visas, M-142 tillägget.
 *    Meningen "Products for your model are shown first." i brödtexten visas
 *    bara när gruppen visas, M-144.
 *
 * Jämförelsen mellan modellen och Compatible models bortser från stora och
 * små bokstäver och mellanslag i kanterna, M-009.
 *
 * Värden som inte kommer från butiken eller en token:
 * - data-nudient-models och |: produktens modeller och skiljetecknet.
 * - filter.p.m.attribute.compatible_models: filtrets parameter, M-013, läst
 *   ur window.nudientFilter när den finns.
 * - section_id: Shopifys parameter för Section Rendering API.
 * - 12 och mer än hälften: de främsta produkterna som avgör om sökningen
 *   handlar om skal, M-144.
 * - data-nudient-match: kortets märke för en riktig träff.
 * - [model]: platsen för modellens namn i rubriken.
 * Stilarna står i assets/nudient-search.css.
 */

import { getStoredModel, findModel, getModels } from '@nudient/model-store';

const norm = (value) => String(value || '').trim().toLowerCase();

/* Handlar sökningen om skal, M-144: fler än hälften av de främsta
   produkterna har en telefonmodell bland sina kompatibla modeller.
   Telefonmodellerna är modellminnets poster. */
const TOP = 12;
let phoneNames = null;
function isPhoneProduct(models) {
  if (!phoneNames) phoneNames = new Set(getModels().flatMap((m) => [norm(m.model), norm(m.name)]).filter(Boolean));
  return String(models || '').split('|').some((v) => phoneNames.has(norm(v)));
}
function mostlyPhones(elements) {
  const top = elements.slice(0, TOP);
  if (!top.length) return false;
  return top.filter((el) => isPhoneProduct(el.dataset.nudientModels)).length * 2 > top.length;
}

function chosenModel() {
  const model = findModel(getStoredModel());
  if (!model) return null;
  return model;
}

/* 1. Sökrutans förslag */

const fits = (li, wanted) => li.dataset.nudientModels.split('|').some((v) => norm(v) === wanted);

async function sortPredictive(list) {
  const model = chosenModel();
  if (!model) return;
  const input = [...document.querySelectorAll('predictive-search-component input[type="search"], input[type="search"][name="q"]')].find((i) => i.value.trim());
  const term = input?.value.trim();
  if (!term || list.dataset.nudientTerm === term) return;
  list.dataset.nudientTerm = term;
  const items = [...list.querySelectorAll(':scope > li[data-nudient-models]')];
  /* Bara när förslagen handlar om skal, och bara inom Shopifys egna förslag
     för sökordet, M-144. */
  if (!mostlyPhones(items)) return;
  const wanted = norm(model.model || model.name);
  const top = items.filter((li) => fits(li, wanted));
  if (!top.length) return;
  const rest = items.filter((li) => !top.includes(li));
  for (const li of [...top, ...rest]) list.appendChild(li);
}

function watchPredictive() {
  const sortAll = () => {
    document.querySelectorAll('.predictive-search-results__wrapper-products').forEach((list) => {
      if (list.querySelector(':scope > li[data-nudient-models]')) sortPredictive(list);
    });
  };
  const observer = new MutationObserver((records) => {
    if (records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && !(n.matches?.('li[data-nudient-models]'))))) sortAll();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  sortAll();
}

/* 2. Sökresultatsidan */

let groupRun = 0;

/** Tar bort gruppen och visar listans dolda kort igen, M-142. */
function clearSearchGroup() {
  document.querySelectorAll('.nudient-search-model-group, .nudient-search-all-heading').forEach((element) => element.remove());
  toggleModelFirst();
  document.querySelectorAll('#ResultsList ul.product-grid > li[data-product-id][hidden]').forEach((li) => {
    li.hidden = false;
  });
}

async function addSearchGroup() {
  const run = ++groupRun;
  const results = document.getElementById('ResultsList');
  const section = results?.closest('[id^="shopify-section-"]');
  /* Huvudlistan, inte gruppens kopia, som saknar data-testid, M-142 */
  const grid = results?.querySelector('ul.product-grid[data-testid="product-grid"]');
  if (!results || !section || !grid) return;
  const model = chosenModel();
  const filter = window.nudientFilter;
  const value = model && filter ? filter.values?.[model.handle] : null;
  /* Gruppen bara när sökningen handlar om skal, M-144 */
  const mainCards = [...grid.querySelectorAll(':scope > li[data-product-id]')];
  if (!value || !mostlyPhones(mainCards)) {
    clearSearchGroup();
    return;
  }
  const url = new URL(window.location.href);
  if (url.searchParams.has(filter.param)) return;
  url.searchParams.delete('page');
  url.searchParams.set(filter.param, value);
  url.searchParams.set('section_id', section.id.replace('shopify-section-', ''));

  let html;
  try {
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) return;
    html = await response.text();
  } catch {
    return;
  }
  if (run !== groupRun) return;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  /* Bara riktiga träffar för sökordet, M-144 */
  const cards = [...doc.querySelectorAll('#ResultsList ul.product-grid > li[data-product-id][data-nudient-match]')];
  clearSearchGroup();
  if (!cards.length || !document.contains(grid)) return;

  const ids = new Set(cards.map((li) => li.dataset.productId));
  const template = document.getElementById('nudient-search-model-heading');
  const heading = (template?.dataset.text || 'For your [model]').replace('[model]', model.name);

  const group = document.createElement('div');
  group.className = 'nudient-search-model-group';
  const title = document.createElement('h2');
  title.className = 'nudient-search-model-group__heading';
  title.textContent = heading;
  const list = grid.cloneNode(false);
  list.removeAttribute('ref');
  list.removeAttribute('data-testid');
  for (const li of cards) {
    li.removeAttribute('ref');
    li.id = `${li.id}-model`;
    list.appendChild(document.importNode(li, true));
  }
  group.append(title, list);
  results.insertBefore(group, results.firstChild);
  /* "All results" över de övriga träffarna, bara när gruppen visas, M-142 */
  const all = document.createElement('h2');
  all.className = 'nudient-search-model-group__heading nudient-search-all-heading';
  all.textContent = template?.dataset.all || 'All results';
  grid.before(all);
  toggleModelFirst();

  for (const li of grid.querySelectorAll(':scope > li[data-product-id]')) {
    if (ids.has(li.dataset.productId)) li.hidden = true;
  }
}

/** Meningen om modellen i brödtexten syns bara när gruppen visas, M-144 */
function toggleModelFirst() {
  const sentence = /** @type {HTMLElement | null} */ (document.querySelector('[data-nudient-model-first]'));
  if (sentence) sentence.hidden = !document.querySelector('.nudient-search-model-group');
}

watchPredictive();
if (document.getElementById('ResultsList') && document.querySelector('.nudient-search')) {
  addSearchGroup();
  /* Byte i väljaren, M-142: gruppen byggs om för den nya modellen */
  document.addEventListener('model:change', () => {
    if (!chosenModel()) {
      groupRun += 1;
      clearSearchGroup();
      return;
    }
    addSearchGroup();
  });
}
